/**
 * Downstream acquisition action forms on lead detail.
 */
import { getDashboardOperator } from "./dashboard-auth.js";
import { escapeAttr, escapeHtml, labelize } from "./dashboard-html.js";
import type { LeadFactoryLead } from "./dashboard-lead-factory-types.js";

function optionHtml(value: string, selected: string): string {
  return `<option value="${escapeAttr(value)}" ${value === selected ? "selected" : ""}>${escapeHtml(labelize(value))}</option>`;
}

const leadFactoryOfferLifecycleStatuses = ["needs_review", "sent", "accepted", "rejected"] as const;
const leadFactoryTitleStatuses = ["in_review", "clear", "issue_found", "cancelled"] as const;
const leadFactoryTitleIssueReasons = [
  "lien_review_needed",
  "tax_issue",
  "probate_issue",
  "bankruptcy_issue",
  "vesting_issue",
  "foreclosure_status_issue",
  "other_typed_review"
] as const;
const leadFactoryClosingArtifactKinds = ["settlement_statement", "deed_recording", "wire_confirmation", "signed_purchase_agreement", "other_typed_closing_artifact"] as const;
const leadFactoryFinanceArtifactKinds = [
  "sale_settlement_statement",
  "repair_invoice",
  "holding_expense_receipt",
  "selling_expense_statement",
  "wire_confirmation",
  "bank_deposit",
  "accounting_reconciliation",
  "other_typed_finance_artifact"
] as const;
const leadFactoryFinanceReconciliationSources = [
  "accounting_system",
  "bank_wire",
  "bank_deposit",
  "settlement_statement",
  "invoice_batch",
  "manual_manager_review"
] as const;
const leadFactoryOwnedPropertyDispositionStatuses = ["review_open", "strategy_selected", "listed", "under_contract", "disposed", "cancelled"] as const;
const leadFactoryOwnedPropertyDispositionStrategies = ["hold", "wholesale", "flip", "rental", "resale"] as const;

export function downstreamAcquisitionFormsHtml(lead: LeadFactoryLead): string {
  return `
    <div class="downstream-action-stack" data-downstream-action-stack="${escapeAttr(lead.id)}">
      ${offerLifecycleFormHtml(lead)}
      ${titleStatusFormHtml(lead)}
      ${closingScheduleFormHtml(lead)}
      ${closingArtifactFormHtml(lead)}
      ${assetOnboardingFormHtml(lead)}
      ${dispositionReviewFormHtml(lead)}
      ${dispositionStatusFormHtml(lead)}
      ${financeRecordFormHtml(lead)}
      ${financeArtifactFormHtml(lead)}
      ${financeReconciliationFormHtml(lead)}
    </div>
  `;
}

function offerLifecycleFormHtml(lead: LeadFactoryLead): string {
  return `
    <form class="structured-action-form downstream-action-form" data-downstream-offer-status-form data-lead-id="${escapeAttr(lead.id)}">
      <select aria-label="Offer status" data-downstream-offer-status>
        ${leadFactoryOfferLifecycleStatuses.map((status) => optionHtml(status, "sent")).join("")}
      </select>
      <input type="text" maxlength="160" value="Dashboard updated offer lifecycle." aria-label="Offer status note" data-downstream-offer-note>
      <button class="button secondary" type="submit">Update offer</button>
    </form>
  `;
}

function titleStatusFormHtml(lead: LeadFactoryLead): string {
  return `
    <form class="structured-action-form downstream-action-form" data-downstream-title-status-form data-lead-id="${escapeAttr(lead.id)}">
      <select aria-label="Title status" data-downstream-title-status>
        ${leadFactoryTitleStatuses.map((status) => optionHtml(status, "clear")).join("")}
      </select>
      <select aria-label="Title issue reason" data-downstream-title-issue-reason>
        ${leadFactoryTitleIssueReasons.map((reason) => optionHtml(reason, "other_typed_review")).join("")}
      </select>
      <input type="text" maxlength="160" value="Dashboard updated title review." aria-label="Title status note" data-downstream-title-note>
      <button class="button secondary" type="submit">Update title</button>
    </form>
  `;
}

function closingScheduleFormHtml(lead: LeadFactoryLead): string {
  return `
    <form class="structured-action-form downstream-action-form" data-downstream-closing-schedule-form data-lead-id="${escapeAttr(lead.id)}">
      <input type="datetime-local" aria-label="Closing date" data-downstream-closing-at>
      <input type="text" maxlength="120" aria-label="Closing location" data-downstream-closing-location>
      <input type="text" maxlength="120" value="${escapeAttr(getDashboardOperator() || "unassigned")}" aria-label="Closing assignee" data-downstream-closing-assigned>
      <input type="text" maxlength="160" value="Dashboard scheduled closing." aria-label="Closing note" data-downstream-closing-note>
      <button class="button secondary" type="submit">Schedule closing</button>
    </form>
  `;
}

function closingArtifactFormHtml(lead: LeadFactoryLead): string {
  return `
    <form class="structured-action-form downstream-action-form" data-downstream-closing-artifact-form data-lead-id="${escapeAttr(lead.id)}">
      <select aria-label="Closing artifact kind" data-downstream-closing-artifact-kind>
        ${leadFactoryClosingArtifactKinds.map((kind) => optionHtml(kind, "settlement_statement")).join("")}
      </select>
      <input type="text" maxlength="120" aria-label="Closing artifact title" data-downstream-closing-artifact-title>
      <input type="text" maxlength="320" aria-label="Closing artifact evidence" data-downstream-closing-artifact-text>
      <input type="text" maxlength="160" aria-label="Closing artifact note" data-downstream-closing-artifact-note>
      <button class="button secondary" type="submit">Attach closing</button>
    </form>
  `;
}

function assetOnboardingFormHtml(lead: LeadFactoryLead): string {
  return `
    <form class="structured-action-form downstream-action-form" data-downstream-asset-onboarding-form data-lead-id="${escapeAttr(lead.id)}">
      <input type="text" maxlength="120" value="${escapeAttr(getDashboardOperator() || "unassigned")}" aria-label="Asset onboarding assignee" data-downstream-asset-assigned>
      <input type="text" maxlength="160" value="Dashboard started owned property onboarding." aria-label="Asset onboarding note" data-downstream-asset-note>
      <button class="button secondary" type="submit">Onboard asset</button>
    </form>
  `;
}

function dispositionReviewFormHtml(lead: LeadFactoryLead): string {
  return `
    <form class="structured-action-form downstream-action-form" data-downstream-disposition-review-form data-lead-id="${escapeAttr(lead.id)}">
      <select aria-label="Disposition strategy" data-downstream-disposition-strategy>
        ${leadFactoryOwnedPropertyDispositionStrategies.map((strategy) => optionHtml(strategy, "resale")).join("")}
      </select>
      <select aria-label="Disposition status" data-downstream-disposition-status>
        ${leadFactoryOwnedPropertyDispositionStatuses.map((status) => optionHtml(status, "strategy_selected")).join("")}
      </select>
      <input type="number" min="0" step="1000" aria-label="Disposition target price" data-downstream-disposition-target-price>
      <input type="text" maxlength="120" value="${escapeAttr(getDashboardOperator() || "unassigned")}" aria-label="Disposition assignee" data-downstream-disposition-assigned>
      <input type="text" maxlength="160" value="Dashboard recorded owned property disposition strategy." aria-label="Disposition note" data-downstream-disposition-note>
      <button class="button secondary" type="submit">Plan disposition</button>
    </form>
  `;
}

function dispositionStatusFormHtml(lead: LeadFactoryLead): string {
  return `
    <form class="structured-action-form downstream-action-form" data-downstream-disposition-status-form data-lead-id="${escapeAttr(lead.id)}">
      <select aria-label="Disposition outcome" data-downstream-disposition-outcome>
        ${leadFactoryOwnedPropertyDispositionStatuses.map((status) => optionHtml(status, "disposed")).join("")}
      </select>
      <input type="number" min="0" step="1000" aria-label="Disposition outcome price" data-downstream-disposition-outcome-price>
      <input type="text" maxlength="120" value="${escapeAttr(getDashboardOperator() || "unassigned")}" aria-label="Disposition outcome assignee" data-downstream-disposition-outcome-assigned>
      <input type="text" maxlength="160" value="Dashboard marked owned property disposition completed." aria-label="Disposition outcome note" data-downstream-disposition-outcome-note>
      <button class="button secondary" type="submit">Close disposition</button>
    </form>
  `;
}

function financeRecordFormHtml(lead: LeadFactoryLead): string {
  return `
    <form class="structured-action-form downstream-action-form finance-record-form" data-downstream-finance-record-form data-lead-id="${escapeAttr(lead.id)}">
      <input type="number" min="0" step="1000" aria-label="Sale proceeds" data-downstream-finance-sale-price>
      <input type="datetime-local" aria-label="Sale closed at" data-downstream-finance-sale-closed-at>
      <input type="number" min="0" step="100" aria-label="Repair expenses" data-downstream-finance-repair-expenses>
      <input type="number" min="0" step="100" aria-label="Holding expenses" data-downstream-finance-holding-expenses>
      <input type="number" min="0" step="100" aria-label="Selling expenses" data-downstream-finance-selling-expenses>
      <input type="number" min="0" step="100" aria-label="Other expenses" data-downstream-finance-other-expenses>
      <input type="text" maxlength="160" aria-label="Finance note" data-downstream-finance-note>
      <button class="button secondary" type="submit">Record finance</button>
    </form>
  `;
}

function financeArtifactFormHtml(lead: LeadFactoryLead): string {
  return `
    <form class="structured-action-form downstream-action-form finance-artifact-form" data-downstream-finance-artifact-form data-lead-id="${escapeAttr(lead.id)}">
      <select aria-label="Finance artifact kind" data-downstream-finance-artifact-kind>
        ${leadFactoryFinanceArtifactKinds.map((kind) => optionHtml(kind, "sale_settlement_statement")).join("")}
      </select>
      <input type="text" maxlength="120" aria-label="Finance artifact title" data-downstream-finance-artifact-title>
      <input type="text" maxlength="360" aria-label="Finance artifact evidence" data-downstream-finance-artifact-text>
      <input type="text" maxlength="160" aria-label="Finance artifact note" data-downstream-finance-artifact-note>
      <button class="button secondary" type="submit">Attach finance</button>
    </form>
  `;
}

function financeReconciliationFormHtml(lead: LeadFactoryLead): string {
  return `
    <form class="structured-action-form downstream-action-form finance-reconciliation-form" data-downstream-finance-reconciliation-form data-lead-id="${escapeAttr(lead.id)}">
      <select aria-label="Finance reconciliation source" data-downstream-finance-reconciliation-source>
        ${leadFactoryFinanceReconciliationSources.map((source) => optionHtml(source, "bank_deposit")).join("")}
      </select>
      <input type="number" min="0" step="100" aria-label="Expected reconciliation amount" data-downstream-finance-reconciliation-expected>
      <input type="number" min="0" step="100" aria-label="Observed reconciliation amount" data-downstream-finance-reconciliation-observed>
      <input type="number" min="0" step="1" aria-label="Reconciliation tolerance" data-downstream-finance-reconciliation-tolerance>
      <input type="text" maxlength="120" aria-label="Reconciliation reference" data-downstream-finance-reconciliation-reference>
      <input type="text" maxlength="160" aria-label="Reconciliation note" data-downstream-finance-reconciliation-note>
      <button class="button secondary" type="submit">Reconcile</button>
    </form>
  `;
}

